import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, MapPin, ExternalLink } from "lucide-react";

import PublicLayout from "@/components/layout/PublicLayout";
import Seo from "@/components/Seo";
import { FlagBE, FlagNL, FlagsBENL } from "@/components/Flags";
import { BRANCHES, CITIES_BE, CITIES_NL } from "@/lib/landingPages";

const MAIN_PAGES = [
  { to: "/", label: "Home" },
  { to: "/hoe-werkt-het", label: "Hoe werkt het" },
  { to: "/prijzen", label: "Prijzen" },
  { to: "/over-ons", label: "Over ons" },
  { to: "/faq", label: "Veelgestelde vragen" },
  { to: "/contact", label: "Contact" },
  { to: "/blog", label: "Blog" },
];

const ANALYSE_PAGES = [
  { to: "/analyse", label: "Start een offerte-analyse" },
  { to: "/credits", label: "Mijn 3-pack credits" },
];

const B2B_PAGES = [
  { to: "/voor-bedrijven", label: "Voor bedrijven — overzicht" },
  { to: "/voor-bedrijven/aanmelden", label: "Bedrijf aanmelden" },
];

const LEGAL_PAGES = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/algemene-voorwaarden", label: "Algemene Voorwaarden" },
  { to: "/cookiebeleid", label: "Cookiebeleid" },
];

export default function SitemapPage() {
  const totalPages =
    MAIN_PAGES.length +
    ANALYSE_PAGES.length +
    B2B_PAGES.length +
    LEGAL_PAGES.length +
    BRANCHES.length +
    BRANCHES.length * (CITIES_BE.length + CITIES_NL.length);

  return (
    <PublicLayout>
      <Seo
        title="Sitemap — alle pagina's van OfferteExpert"
        description="Complete sitemap van OfferteExpert: hoofdpagina's, offertes per branche, offertes per stad in België en Nederland, juridische pagina's en meer."
      />

      <Hero totalPages={totalPages} />

      <section className="oc-section bg-white">
        <div className="oc-container max-w-6xl space-y-16">
          <Group title="Hoofdpagina's" subtitle={`${MAIN_PAGES.length} pagina's`}>
            <SimpleList items={MAIN_PAGES} />
          </Group>

          <Group title="Offerte-analyse" subtitle={`${ANALYSE_PAGES.length} pagina's`}>
            <SimpleList items={ANALYSE_PAGES} highlight />
          </Group>

          <Group title="Voor bedrijven" subtitle={`${B2B_PAGES.length} pagina's`}>
            <SimpleList items={B2B_PAGES} />
          </Group>

          <BrancheSection />

          <Group title="Juridisch" subtitle={`${LEGAL_PAGES.length} pagina's`}>
            <SimpleList items={LEGAL_PAGES} muted />
          </Group>

          <XmlSitemapBlock />
        </div>
      </section>
    </PublicLayout>
  );
}

/* ─── Sections ─── */
function Hero({ totalPages }) {
  return (
    <section className="relative oc-hero-gradient overflow-hidden text-white">
      <div className="absolute inset-0 oc-grid-bg opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#003B82]/65 via-[#003B82]/50 to-[#003B82]/85" />
      <div className="relative oc-container py-16 sm:py-20 max-w-5xl">
        <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[12px] uppercase tracking-[0.12em] font-semibold text-white/90">
          <FlagsBENL size="sm" /> Sitemap · België & Nederland
        </div>
        <h1 className="font-display font-extrabold text-[40px] sm:text-[56px] lg:text-[64px] leading-[1.05] tracking-tight mt-5 text-white">
          Alle pagina's van OfferteExpert
        </h1>
        <p className="mt-5 text-white/85 text-[17px] sm:text-[19px] leading-[1.6] max-w-3xl">
          Vind je weg door alle <strong className="text-white">{totalPages.toLocaleString("nl-BE")}</strong> pagina's
          van het platform — hoofdmenu, offerte-analyse, partner-portaal, branche- en stadspagina's
          voor heel België en Nederland.
        </p>
      </div>
    </section>
  );
}

function Group({ title, subtitle, children }) {
  return (
    <div data-testid={`sitemap-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-end justify-between border-b border-oc-border pb-4">
        <h2 className="font-display font-bold text-[24px] sm:text-[28px] text-oc-primary tracking-tight">{title}</h2>
        <span className="text-[12px] uppercase tracking-[0.1em] font-semibold text-oc-textSecondary">{subtitle}</span>
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function SimpleList({ items, highlight = false, muted = false }) {
  return (
    <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
      {items.map((p) => (
        <li key={p.to}>
          <Link
            to={p.to}
            className={`group flex items-center justify-between py-2 border-b border-oc-border/60 transition-colors ${
              muted ? "text-oc-textSecondary hover:text-oc-primary" : "text-oc-primary hover:text-oc-secondary"
            } ${highlight ? "font-semibold" : ""}`}
          >
            <span className="text-[15px]">{p.label}</span>
            <span className="text-[12px] text-oc-textSecondary/60 group-hover:text-oc-secondary group-hover:translate-x-0.5 transition-all font-mono">
              {p.to}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function BrancheSection() {
  const total = BRANCHES.length + BRANCHES.length * (CITIES_BE.length + CITIES_NL.length);
  return (
    <div data-testid="sitemap-group-branches">
      <div className="flex items-end justify-between border-b border-oc-border pb-4">
        <div>
          <h2 className="font-display font-bold text-[24px] sm:text-[28px] text-oc-primary tracking-tight">
            Offertes per branche &amp; stad
          </h2>
          <p className="mt-2 text-oc-textSecondary text-[14px]">
            {BRANCHES.length} branches × {CITIES_BE.length + CITIES_NL.length} steden = <strong>{total}</strong> landingspagina's voor België &amp; Nederland.
          </p>
        </div>
        <span className="text-[12px] uppercase tracking-[0.1em] font-semibold text-oc-textSecondary">
          {total.toLocaleString("nl-BE")} pagina's
        </span>
      </div>
      <div className="mt-8 space-y-3">
        {BRANCHES.map((b) => (
          <BrancheCard key={b.slug} branche={b} />
        ))}
      </div>
    </div>
  );
}

function BrancheCard({ branche }) {
  const [open, setOpen] = useState(false);
  const Icon = branche.icon;
  return (
    <div className="bg-white border border-oc-border rounded-oc overflow-hidden transition-shadow hover:shadow-oc-card-hover">
      <div className="flex items-center gap-4 p-5">
        <div className="w-11 h-11 rounded-xl bg-oc-subtle flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-oc-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <Link
            to={`/offertes/${branche.slug}`}
            className="font-display font-semibold text-oc-primary text-[17px] hover:text-oc-secondary"
          >
            {branche.name}
          </Link>
          <p className="text-oc-textSecondary text-[13px] mt-0.5">
            Hoofdpagina + {CITIES_BE.length + CITIES_NL.length} stadspagina's
          </p>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-[13px] font-semibold text-oc-secondary inline-flex items-center gap-1 hover:text-oc-primary transition-colors shrink-0"
          data-testid={`branche-toggle-${branche.slug}`}
          aria-expanded={open}
        >
          {open ? "Verberg steden" : "Toon alle steden"}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="border-t border-oc-border bg-oc-subtle/40 px-5 py-6">
          <div className="grid md:grid-cols-2 gap-8">
            <CityColumn title="België" icon={<FlagBE className="w-3.5 h-2.5 rounded-[1px] ring-1 ring-black/10" />} cities={CITIES_BE} brancheSlug={branche.slug} />
            <CityColumn title="Nederland" icon={<FlagNL className="w-3.5 h-2.5 rounded-[1px] ring-1 ring-black/10" />} cities={CITIES_NL} brancheSlug={branche.slug} />
          </div>
        </div>
      )}
    </div>
  );
}

function CityColumn({ title, icon, cities, brancheSlug }) {
  return (
    <div>
      <h3 className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.1em] font-semibold text-oc-textSecondary mb-3">
        {icon} {title} <span className="text-oc-textSecondary/60 font-mono normal-case">({cities.length})</span>
      </h3>
      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5">
        {cities.map((c) => (
          <li key={c.slug}>
            <Link
              to={`/offertes/${brancheSlug}/${c.slug}`}
              className="text-[13px] text-oc-primary hover:text-oc-secondary inline-flex items-center gap-1 group"
            >
              <MapPin className="w-2.5 h-2.5 text-oc-textSecondary/60 group-hover:text-oc-secondary" />
              {c.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function XmlSitemapBlock() {
  const xmlUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sitemap.xml`;
  return (
    <div className="bg-oc-subtle border border-oc-border rounded-oc p-8">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-white border border-oc-border flex items-center justify-center shrink-0">
          <MapPin className="w-5 h-5 text-oc-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-[20px] text-oc-primary">XML-sitemap voor zoekmachines</h2>
          <p className="mt-2 text-oc-textSecondary text-[14px] leading-relaxed">
            Gebruik deze XML-sitemap voor Google Search Console, Bing Webmaster Tools en andere SEO-tools.
            Hij wordt automatisch bijgewerkt bij elke nieuwe blog-post of landingspagina.
          </p>
          <a
            href={xmlUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-oc-secondary hover:text-oc-primary font-semibold text-[14px] font-mono break-all"
            data-testid="xml-sitemap-link"
          >
            {xmlUrl} <ExternalLink className="w-4 h-4 shrink-0" />
          </a>
        </div>
      </div>
    </div>
  );
}
